import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Route, Router, Switch } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import 'firebase/functions';
import { getRemoteConfig } from 'firebase/remote-config';
import mixpanel from 'mixpanel-browser';
import 'sanitize.css/sanitize.css';
import { StyleSheetManager } from 'styled-components';
import { shouldForwardProp } from 'styled-components-karnott';
import WebFont from 'webfontloader';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { setUnauthorizedHandler } from './api/api';
import { saveState } from './api/localStorage';
import { queryClient } from './api/queryClient';
import Loader from './components/Loader';
import { MapProvider } from './components/map/contexts/map';
import {
  FIREBASE_API_KEY,
  FIREBASE_APP_ID,
  FIREBASE_MEASUREMENT_ID,
  FIREBASE_PROJECT_ID,
  MIXPANEL_KEY,
} from './constants';
import AccessToken from './containers/AccessTokenContainer';
import App from './containers/AppContainer';
import LogoutContainer from './containers/LogoutContainer';
import ShareContainer from './containers/ShareContainer';
import UnpaidContainer from './containers/UnpaidContainer';
import I18nProvider from './contexts/I18nProvider';
import { PermissionsProvider } from './contexts/PermissionsProvider';
import { PluginsProvider } from './contexts/PluginsProvider';
import { UIProvider } from './contexts/UIProvider';
import { userLoggedOut } from './slices/users';
import store, { history } from './store';
import './stylesheets/style.css';
import WithAnalytics from './utils/Analytics';
import { RemoteConfig, RemoteConfigSwitch } from './utils/RemoteConfig';
import Sentry from './utils/Sentry';

// Missing globals
window.global = window;
// https://github.com/Leaflet/Leaflet.draw/issues/1026
window.type = '';

if (import.meta.env.PROD) {
  // MIXPANEL
  mixpanel.init(MIXPANEL_KEY, { debug: false, track_pageview: true });

  // SENTRY
  Sentry.getInstance().init();

  // Init Firebase
  const config = {
    appId: FIREBASE_APP_ID,
    apiKey: FIREBASE_API_KEY,
    projectId: FIREBASE_PROJECT_ID,
    measurementId: FIREBASE_MEASUREMENT_ID,
  };

  initializeApp(config);
  const r = indexedDB.open('ping', 1);
  r.onsuccess = function () {
    const remoteConfig = getRemoteConfig();
    RemoteConfig.getInstance().setConfig(remoteConfig);
    indexedDB.deleteDatabase('ping');
  };
  r.onerror = function () {
    return false;
  };
}

store.subscribe(() => {
  const state = store.getState();
  const { user } = state;
  saveState({ user });
});

setUnauthorizedHandler(() => store.dispatch(userLoggedOut()));

// Unregister existing Service Worker
if (navigator.serviceWorker) {
  navigator.serviceWorker
    .getRegistrations()
    .then(function (registrations) {
      for (const registration of registrations) {
        registration.unregister();
      }
    })
    .catch((e) => console.log(e));
}

createRoot(document.getElementById('root')).render(
  <StyleSheetManager shouldForwardProp={shouldForwardProp} enableVendorPrefixes>
    <Provider store={store}>
      <Router history={history}>
        <I18nProvider>
          <QueryClientProvider client={queryClient}>
            {import.meta.env.DEV && import.meta.env.MODE !== 'test' ? (
              <ReactQueryDevtools initialIsOpen={false} />
            ) : null}
            <RemoteConfigSwitch>
              <UIProvider>
                <PermissionsProvider>
                  <PluginsProvider>
                    <MapProvider>
                      <Switch>
                        <Route path="/oauth_access_token" component={WithAnalytics(AccessToken)} />
                        <Route path="/share" component={WithAnalytics(ShareContainer)} />
                        <Route path="/loading" component={Loader} />
                        <Route path="/unpaid" component={UnpaidContainer} />
                        <Route path="/logout" component={LogoutContainer} />
                        <Route component={WithAnalytics(App)} />
                      </Switch>
                    </MapProvider>
                  </PluginsProvider>
                </PermissionsProvider>
              </UIProvider>
            </RemoteConfigSwitch>
          </QueryClientProvider>
        </I18nProvider>
      </Router>
    </Provider>
  </StyleSheetManager>,
);

WebFont.load({
  google: {
    families: ['Montserrat:400,700,800,800i', 'Open Sans:400,600,700', 'Ubuntu:100,200,300,400,400i,500,600,700,700i'],
  },
});
